export type SocialPlatformType =
  | 'X'
  | 'Discord'
  | 'Telegram'
  | 'Facebook'
  | 'Reddit'
  | 'LinkedIn'
  | 'Email';

export interface PlatformDetails {
  title?: string;
  message?: string;
  url?: string;
  via?: string;
  appName?: string;
  feedbackType?: 'Review' | 'Suggestion';
}

const URL_PLACEHOLDER = '{{url}}';
const APP_NAME_PLACEHOLDER = '{{appName}}';
const FEEDBACK_TYPE_PLACEHOLDER = '{{feedbackType}}';

export type SocialLinkGenerator = (props: PlatformDetails) => string;

const convertObjectToURLParams = (obj: Record<string, unknown>): string => {
  const params = Object.entries(obj)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);

  return params.length > 0 ? `?${params.join('&')}` : '';
};

export const formatMessageWithPlaceholders = (
  message: string,
  link = '',
  appName?: string,
  feedbackType?: string,
): string => {
  const formatted = message
    .replace(URL_PLACEHOLDER, link)
    .replace(APP_NAME_PLACEHOLDER, appName || '')
    .replace(FEEDBACK_TYPE_PLACEHOLDER, feedbackType || '');

  const hadAnyPlaceholder =
    message.includes(URL_PLACEHOLDER) ||
    message.includes(APP_NAME_PLACEHOLDER) ||
    message.includes(FEEDBACK_TYPE_PLACEHOLDER);

  return hadAnyPlaceholder ? formatted : message ? `${message} ${link}` : link;
};

/**
 * A set of functions that generate share URLs for various social platforms.
 */
export const socialLinkGenerators: Record<SocialPlatformType, SocialLinkGenerator> = {
  /**
   * Generates a `mailto:` link for sharing via email.
   * Uses the `subject` and `body` query params.
   */
  Email: ({ url, title = '', message = '', appName, feedbackType }) => {
    message = formatMessageWithPlaceholders(message, url, appName, feedbackType);
    return `mailto:${convertObjectToURLParams({ subject: title, body: message })}`;
  },

  /**
   * Generates a Facebook share link.
   * Note: Facebook no longer supports `title` parameter.
   */
  Facebook: ({ url }) => {
    return `https://www.facebook.com/sharer/sharer.php${convertObjectToURLParams({ u: url })}`;
  },

  /**
   * Generates a LinkedIn share link using the provided URL.
   */
  LinkedIn: ({ url }) => {
    return `https://www.linkedin.com/sharing/share-offsite${convertObjectToURLParams({ url })}`;
  },

  /**
   * Generates a Telegram share link.
   * Note: The `url` param is required; it can also act as the title.
   */
  Telegram: ({ url, message = '', appName, feedbackType }) => {
    message = formatMessageWithPlaceholders(message, url, appName, feedbackType);
    return `https://t.me/share/url${convertObjectToURLParams({ text: message, url })}`;
  },

  /**
   * Generates a link to open the Discord app.
   * Note: Discord does not support URL-based message sharing.
   * The message should be copied to clipboard and pasted manually.
   */
  Discord: () => {
    return `https://discord.com/app`;
  },

  /**
   * Generates a Twitter (X) share link.
   * Note: Twitter appends the URL to the text before the `via` tag, separated by a space.
   */
  X: ({ url, message = '', via = 'MagicSquareio', appName, feedbackType }) => {
    message = formatMessageWithPlaceholders(message, url, appName, feedbackType);
    return `https://twitter.com/share${convertObjectToURLParams({ text: message, via })}`;
  },

  /**
   * Generates a Reddit share link with support for `title`, `text`, and `url`.
   */
  Reddit: ({ url, message = '', title = '', appName, feedbackType }) => {
    message = formatMessageWithPlaceholders(message, url, appName, feedbackType);
    return `https://www.reddit.com/submit${convertObjectToURLParams({
      url,
      text: message,
      title,
    })}`;
  },
};

export const generatePlatformShareLink = (
  platform: SocialPlatformType,
  config: PlatformDetails,
) => {
  const linkGenerator = socialLinkGenerators[platform];

  return linkGenerator ? linkGenerator(config) : '';
};
